import React, { useEffect, useState, useCallback } from 'react';
import { Search, Plus, Trash2, X, MapPin, Mail } from 'lucide-react';
import api from '../utils/api';

// Initials avatar helper
const Initials = ({ name }) => {
  const parts = (name || '').trim().split(' ');
  const initials = parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('');
  return <div className="avatar">{initials}</div>;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', password: '' });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await api.get('/admin/customers', { params: { search } });
      setCustomers(res.data.data.customers);
    } catch (err) {
      console.error('Fetch customers error:', err);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(fetchCustomers, 300);
    return () => clearTimeout(timer);
  }, [fetchCustomers]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await api.delete(`/admin/users/${id}`);
      setCustomers((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      alert('Failed to delete user');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      await api.post('/admin/users', { ...form, role: 'customer' });
      setShowModal(false);
      setForm({ full_name: '', email: '', phone: '', password: '' });
      fetchCustomers();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-wrap">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Customer Management</h1>
          <p className="page-subtitle">Manage customers</p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)} id="add-customer-btn">
          <Plus size={16} /> Add Customer
        </button>
      </div>

      {/* Search */}
      <div className="search-wrap">
        <Search size={16} className="search-icon" />
        <input
          id="customer-search"
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Table */}
      <div className="card table-card">
        {loading ? (
          <div className="page-loading">Loading...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>USER</th>
                <th>CONTACT</th>
                <th>LOCATION</th>
                <th>JOINED</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty-row">No customers found</td>
                </tr>
              ) : (
                customers.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="user-cell">
                        <Initials name={c.full_name} />
                        <span className="user-name">{c.full_name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="contact-cell">
                        <Mail size={13} className="contact-icon" />
                        <a href={`mailto:${c.email}`} className="contact-link">{c.email}</a>
                      </div>
                    </td>
                    <td>
                      <div className="location-cell">
                        <MapPin size={13} className="location-icon" />
                        <span>{[c.city, c.state].filter(Boolean).join(', ') || '—'}</span>
                      </div>
                    </td>
                    <td className="date-cell">{formatDate(c.created_at)}</td>
                    <td>
                      <button
                        className="icon-btn danger"
                        onClick={() => handleDelete(c.id)}
                        title="Delete customer"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add New Customer</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="modal-form">
              {formError && <div className="error-banner">{formError}</div>}
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input
                  className="form-input"
                  required
                  value={form.full_name}
                  onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  className="form-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  className="form-input"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Password *</label>
                <input
                  className="form-input"
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
